/**
 * Extract error message from API error response
 * @param {Error} error - The error object from axios
 * @returns {string} - The extracted error message
 */
export const extractErrorMessage = (error) => {
  // Log error for debugging in development
  if (process.env.NODE_ENV === "development") {
    console.error("API Error:", error);
    console.error("Error response:", error.response);
    console.error("Error data:", error.response?.data);
  }

  // Network error (no response)
  if (!error.response) {
    return error.message || "Network error. Please check your connection.";
  }

  const { data, status } = error.response;

  // Handle null/undefined data
  if (!data) {
    return getHttpStatusMessage(status);
  }

  // Handle different response structures
  if (data) {
    // Laravel validation errors: { message: "...", errors: { field: ["error"] } }
    if (data.message) {
      // If there's validation errors, extract specific field errors
      if (
        data.errors &&
        typeof data.errors === "object" &&
        !Array.isArray(data.errors)
      ) {
        try {
          const validationErrors = Object.values(data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          if (validationErrors.length > 0) {
            return validationErrors.join(", ");
          }
        } catch (e) {
          // Fallback to message if extraction fails
          if (process.env.NODE_ENV === "development") {
            console.error("Error extracting validation errors:", e);
          }
        }
      }
      // Also check for data.data structure (alternative Laravel format)
      if (
        data.data &&
        typeof data.data === "object" &&
        !Array.isArray(data.data)
      ) {
        try {
          const validationErrors = Object.values(data.data)
            .filter((value) => Array.isArray(value))
            .flat();
          if (validationErrors.length > 0) {
            return validationErrors.join(", ");
          }
        } catch (e) {
          // Fallback to message if extraction fails
          if (process.env.NODE_ENV === "development") {
            console.error("Error extracting validation errors:", e);
          }
        }
      }
      return data.message;
    }

    // Alternative error field
    if (data.error) {
      return data.error;
    }

    // Array of errors
    if (Array.isArray(data)) {
      return data.join(", ");
    }

    // String response
    if (typeof data === "string") {
      return data;
    }
  }

  // HTTP status based messages
  return getHttpStatusMessage(status);
};

/**
 * Check if error is a network error that is being handled by the global modal
 * @param {Error} error - The error object from axios
 * @returns {boolean} - True if this is a network error handled by the modal
 */
export const isNetworkErrorHandledByModal = (error) => {
  return error?.isNetworkError === true;
};

/**
 * Get HTTP status message
 * @param {number} status - HTTP status code
 * @returns {string} - Status message
 */
const getHttpStatusMessage = (status) => {
  const statusMessages = {
    400: "Bad request. Please check your input.",
    401: "Unauthorized. Please log in.",
    403: "Forbidden. You do not have permission.",
    404: "Resource not found.",
    419: "Session expired. Please refresh the page.",
    422: "Validation error. Please check your input.",
    429: "Too many requests. Please try again later.",
    500: "Server error. Please try again later.",
    502: "Service unavailable. Please try again later.",
    503: "Service temporarily unavailable.",
  };

  return statusMessages[status] || "An error occurred. Please try again.";
};
